/**
 * Workshops & Vorträge (/workshops/[slug]).
 *
 * Eigenes Geschäftsfeld: Schulungen und Vorträge zu KI und Digitalisierung
 * für Unternehmen, Lehrlinge und Schulen. Nutzt dasselbe Inhaltsmodell wie
 * Branchen und Lösungen, damit der Aufbau der Website durchgängig bleibt.
 *
 * WICHTIG: Hier stehen keine erfundenen Referenzen, Teilnehmerzahlen oder
 * Bewertungen. Was nicht belegt ist, steht nicht drin.
 */

import type { LandingContent } from "./landing";

/** Zusatzangaben, die nur Schulungen haben (fließen ins Course-Schema). */
export type WorkshopMeta = {
  /** Für wen das Angebot gedacht ist. */
  audience: string;
  /** Übliches Format. */
  format: string;
  /** Übliche Dauer. */
  duration: string;
  /** Was Teilnehmende danach können – zugleich Lernziele im Schema. */
  outcomes: string[];
};

export type Workshop = LandingContent & WorkshopMeta;

export const workshops: Workshop[] = [
  // ────────────────────────────────────────────────────────────────
  {
    slug: "ki-workshop-unternehmen",
    icon: "sparkles",
    title: "KI-Workshop für Unternehmen",
    navTitle: "KI-Workshop",
    tagline: "Was KI im Betrieb wirklich kann – und was nicht.",
    audience: "Geschäftsführung, Führungskräfte und Teams in KMU",
    format: "Inhouse bei Ihnen, bei uns in Wien oder online",
    duration: "Halbtag oder Ganztag",
    outcomes: [
      "Einschätzen, welche eigenen Aufgaben sich für KI eignen",
      "Werkzeuge sicher und datenschutzkonform einsetzen",
      "Ergebnisse einer KI kritisch prüfen statt blind übernehmen",
      "Einen ersten Anwendungsfall im eigenen Betrieb benennen",
    ],
    summary:
      "Ein Praxisworkshop ohne Hype: Ihr Team lernt, welche Aufgaben sich für KI eignen, wie die Werkzeuge sicher eingesetzt werden und wo die Grenzen liegen – am eigenen Arbeitsalltag statt an Beispielen aus dem Lehrbuch.",
    keywords: [
      "KI Workshop Unternehmen",
      "KI Schulung Wien",
      "künstliche Intelligenz Weiterbildung KMU",
      "ChatGPT Schulung Firma",
      "KI Inhouse Schulung Österreich",
    ],
    metaTitle: "KI-Workshop für Unternehmen | Wien",
    metaDescription:
      "Praxisworkshop zu KI im Betrieb: welche Aufgaben sich eignen, wie Werkzeuge sicher genutzt werden, wo die Grenzen liegen. Jetzt Termin anfragen.",
    heroEyebrow: "Workshop",
    heroHeadline: "Ihr Team nutzt KI längst – nur weiß niemand, ob richtig",
    heroSub:
      "In fast jedem Betrieb probieren einzelne Mitarbeiter KI-Werkzeuge aus, meist ohne Regeln und ohne Wissen über die Grenzen. Dieser Workshop macht daraus einen bewussten, sicheren Umgang – an Ihren eigenen Aufgaben.",
    pains: [
      {
        title: "Jeder macht es anders – oder heimlich",
        text: "Ohne klare Regeln nutzen manche KI intensiv, andere gar nicht. Und wer Firmendaten in ein beliebiges Werkzeug tippt, tut das oft, ohne die Folgen zu kennen.",
      },
      {
        title: "Zwischen Hype und Ablehnung fehlt die Mitte",
        text: "Die einen erwarten Wunder, die anderen halten alles für Spielerei. Beides führt dazu, dass die Fälle liegen bleiben, in denen KI tatsächlich Zeit spart.",
      },
      {
        title: "Niemand prüft die Ergebnisse",
        text: "KI antwortet immer selbstsicher, auch wenn sie falsch liegt. Wer nicht gelernt hat, das zu erkennen, übernimmt Fehler ungeprüft in Angebote oder Kundenmails.",
      },
      {
        title: "Datenschutz ist ungeklärt",
        text: "Dürfen Kundendaten hinein? Personaldaten? Die meisten Betriebe haben darauf keine Antwort – und genau deshalb entstehen die Probleme im Verborgenen.",
      },
    ],
    solutions: [
      {
        title: "An Ihren echten Aufgaben",
        text: "Wir arbeiten nicht mit Musterbeispielen, sondern mit dem, was Ihr Team täglich tut. Vorab sammeln wir typische Aufgaben ein und bauen den Workshop darum herum.",
      },
      {
        title: "Klare Regeln statt Verbote",
        text: "Am Ende steht eine kurze, verständliche Richtlinie: Welche Daten dürfen hinein, welche nicht, und was muss immer ein Mensch prüfen. Eine Seite, nicht dreißig.",
      },
      {
        title: "Grenzen erkennen lernen",
        text: "Der wichtigste Teil: Woran merke ich, dass die Antwort erfunden ist? Wir üben das an Beispielen, bei denen die KI überzeugend danebenliegt.",
      },
    ],
    sections: [
      {
        heading: "Was im Workshop passiert",
        body:
          "Wir beginnen mit einer ehrlichen Einordnung: Was können die heutigen Werkzeuge tatsächlich, wie funktionieren sie im Groben, und woher kommen ihre Fehler. Danach arbeiten die Teilnehmenden an eigenen Aufgaben – Angebotstexte, Kundenanfragen, Dokumente zusammenfassen, Recherchen. Wir zeigen, wie man Anfragen so formuliert, dass brauchbare Ergebnisse entstehen, und vor allem, wie man erkennt, wann ein Ergebnis nicht stimmt. Zum Abschluss halten wir gemeinsam fest, welche zwei bis drei Anwendungsfälle sich im Betrieb sofort lohnen und welche Regeln dafür gelten sollen. Sie gehen nicht mit Begeisterung nach Hause, sondern mit einer Liste.",
      },
      {
        heading: "Warum wir mit Grenzen statt mit Möglichkeiten anfangen",
        body:
          "Die meisten KI-Schulungen zeigen, was alles geht. Das erzeugt Begeisterung, die nach zwei Wochen verfliegt, weil im Alltag die Fälle nicht auftauchen. Wichtiger ist die umgekehrte Richtung: Wo darf ich mich nicht darauf verlassen? Ein Mitarbeiter, der weiß, dass die KI Zahlen, Namen und Rechtsauskünfte erfinden kann, nutzt sie souverän. Einer, der das nicht weiß, ist ein Risiko – unabhängig davon, wie gut er die Werkzeuge bedient. Deshalb steht bei uns das Prüfen im Mittelpunkt, nicht das Beeindrucken.",
      },
      {
        heading: "Datenschutz praktisch statt paragraphenweise",
        body:
          "Wir gehen die Frage durch, die im Alltag zählt: Was passiert eigentlich mit dem, was ich eintippe? Je nach Werkzeug und Vertrag sind die Antworten unterschiedlich, und genau diese Unterschiede erklären wir – ohne Paragraphenvortrag, aber mit klaren Konsequenzen. Daraus entsteht die erwähnte einseitige Richtlinie für Ihren Betrieb. Wichtige Klarstellung: Wir liefern die fachliche Grundlage und praktische Empfehlungen. Die datenschutzrechtliche Bewertung für Ihr Unternehmen bleibt bei Ihnen und Ihrem Datenschutzbeauftragten.",
      },
      {
        heading: "Für wen sich der Workshop lohnt – und für wen nicht",
        body:
          "Sinnvoll ist er für Teams, die mit Texten, Dokumenten, Kundenkommunikation oder Recherche zu tun haben, und für Führungskräfte, die entscheiden müssen, wo investiert wird. Weniger sinnvoll ist er, wenn erwartet wird, dass danach ein fertiges KI-System im Betrieb läuft – das ist ein Umsetzungsprojekt, kein Schulungstag. Und er lohnt sich nicht, wenn niemand die Zeit hat, das Gelernte danach anzuwenden. Wir sagen das im Vorgespräch offen, statt einen Termin zu verkaufen, der verpufft.",
      },
    ],
    deliverables: [
      "Vorgespräch: Wir sammeln Ihre typischen Aufgaben ein",
      "Praxisteil an echten Beispielen aus Ihrem Betrieb",
      "Übungen zum Erkennen falscher KI-Antworten",
      "Einseitige KI-Richtlinie für Ihr Unternehmen",
      "Liste konkreter Anwendungsfälle mit Aufwandseinschätzung",
    ],
    example: {
      title: "Aus heimlicher Nutzung wird bewusster Einsatz",
      persona: "Typisches Szenario: Dienstleister mit 15 Mitarbeitern",
      problem:
        "Einzelne Mitarbeiter nutzten KI-Werkzeuge auf eigene Faust, teils mit Kundendaten. Es gab weder Regeln noch Wissen über die Grenzen.",
      solution:
        "Ganztägiger Workshop an eigenen Aufgaben, gemeinsame Erarbeitung einer einseitigen Richtlinie und Festlegung von zwei Anwendungsfällen für den Start.",
      result:
        "Statt verstreuter Einzelversuche gibt es einen klaren Rahmen – und zwei Aufgaben, bei denen die Zeitersparnis messbar ist.",
    },
    faq: [
      { q: "Braucht unser Team Vorkenntnisse?", a: "Nein. Wir setzen keinerlei technisches Vorwissen voraus. Erfahrene Teilnehmende bekommen im Praxisteil anspruchsvollere Aufgaben, damit niemand unterfordert ist." },
      { q: "Wie viele Personen können teilnehmen?", a: "Sinnvoll sind fünf bis fünfzehn Personen. Darunter lohnt sich der Aufwand selten, darüber leidet der Praxisteil, weil nicht mehr alle mit eigenen Aufgaben arbeiten können." },
      { q: "Findet der Workshop bei uns statt?", a: "Auf Wunsch ja – inhouse ist meist am wirksamsten, weil die eigene Umgebung und die eigenen Systeme vor Ort sind. Alternativ bei uns in Wien oder online." },
      { q: "Was kostet der Workshop?", a: "Das hängt von Format, Dauer und Gruppengröße ab. Sie bekommen nach einem kurzen Vorgespräch einen festen Preis, keine Stundenabrechnung. Für Weiterbildung gibt es in Österreich zudem Fördermöglichkeiten." },
      { q: "Können wir danach Unterstützung bei der Umsetzung bekommen?", a: "Ja, das ist aber getrennt vom Workshop. Wir verkaufen keine Schulung als Einstieg in ein Projekt – wenn Sie das Gelernte selbst umsetzen, ist uns das genauso recht." },
    ],
    relatedServices: ["kuenstliche-intelligenz", "it-consulting"],
    relatedSlugs: ["digitalisierung-workshop", "vortraege-keynotes"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "digitalisierung-workshop",
    icon: "compass",
    title: "Digitalisierungs-Workshop",
    navTitle: "Digitalisierung",
    tagline: "Wo im Betrieb Zeit verloren geht – und was sich lohnt.",
    audience: "Geschäftsführung und Teams in Klein- und Mittelbetrieben",
    format: "Inhouse, bei uns in Wien oder online",
    duration: "Halbtag oder Ganztag",
    outcomes: [
      "Die eigenen Abläufe erfassen und Zeitfresser benennen",
      "Beurteilen, welche Aufgaben sich automatisieren lassen",
      "Aufwand und Nutzen einer Maßnahme grob einschätzen",
      "Eine priorisierte Liste mit ersten Schritten mitnehmen",
    ],
    summary:
      "Digitalisierung scheitert selten an Technik, sondern daran, dass niemand weiß, wo anzufangen ist. Der Workshop macht die eigenen Abläufe sichtbar und ordnet sie nach Aufwand und Nutzen.",
    keywords: [
      "Digitalisierung Workshop KMU",
      "Digitalisierungsberatung Schulung",
      "Prozesse digitalisieren Weiterbildung",
      "Workshop Digitalisierung Wien",
    ],
    metaTitle: "Digitalisierungs-Workshop für KMU | Wien",
    metaDescription:
      "Workshop, der zeigt wo im Betrieb Zeit verloren geht und was sich zu automatisieren lohnt. Mit priorisierter Liste zum Mitnehmen. Jetzt anfragen.",
    heroEyebrow: "Workshop",
    heroHeadline: "Digitalisierung scheitert selten an der Technik",
    heroSub:
      "Sie scheitert daran, dass niemand genau weiß, wo die Zeit hingeht und welcher Schritt sich zuerst lohnt. Genau das erarbeiten wir an einem Tag – mit Ihren Abläufen, nicht mit Best-Practice-Folien.",
    pains: [
      { title: "Alle sagen digitalisieren – niemand sagt was", text: "Der Begriff ist so groß, dass er nichts bedeutet. Solange nicht klar ist, welcher Ablauf gemeint ist, passiert nichts." },
      { title: "Software wurde gekauft und nie genutzt", text: "Ein Werkzeug ohne vorherige Analyse löst selten ein Problem. Es kommt dazu, statt etwas zu ersetzen – und wird deshalb ignoriert." },
      { title: "Niemand weiß, wo die Zeit wirklich hingeht", text: "Gefühlt frisst alles Zeit. Was tatsächlich wie lange dauert, hat selten jemand gemessen – und ohne Messung wird an der falschen Stelle optimiert." },
      { title: "Die Angst, das Falsche zu kaufen", text: "Nachvollziehbar, denn Fehlinvestitionen sind teuer. Das Ergebnis ist oft Stillstand, der auf Dauer noch teurer wird." },
    ],
    solutions: [
      { title: "Abläufe sichtbar machen", text: "Wir zeichnen gemeinsam auf, wie Arbeit tatsächlich durch Ihren Betrieb läuft – nicht wie sie laufen sollte. Die Umwege stehen dann auf dem Papier." },
      { title: "Nach Aufwand und Nutzen sortieren", text: "Jeder gefundene Punkt bekommt eine grobe Einschätzung: Was kostet die Änderung, was bringt sie pro Monat. Manches fällt dabei durch – das ist der Sinn." },
      { title: "Mit dem Kleinsten anfangen", text: "Sie gehen mit ein bis zwei Schritten heraus, die diese Woche machbar sind. Ein Zehn-Punkte-Plan bleibt liegen, zwei Punkte werden erledigt." },
    ],
    sections: [
      {
        heading: "Wie der Workshop abläuft",
        body:
          "Wir starten mit einer einfachen Übung: Jeder Teilnehmende beschreibt seinen typischen Tag und markiert, was sich wiederholt. Daraus entsteht eine Landkarte der Abläufe, in der die Übergaben zwischen Personen und Systemen sichtbar werden – dort sitzen die meisten Verluste. Anschließend bewerten wir jeden Punkt gemeinsam nach zwei Fragen: Wie oft passiert das, und wie lange dauert es. Das ergibt eine Rangfolge, die nicht auf Meinung beruht, sondern auf Häufigkeit mal Dauer. Zum Schluss ordnen wir den obersten Punkten mögliche Lösungswege zu – manchmal Software, oft aber eine Absprache, eine Vorlage oder das Weglassen eines Schritts.",
      },
      {
        heading: "Warum die billigste Lösung oft die beste ist",
        body:
          "In fast jedem Workshop stellt sich heraus, dass ein Teil der Zeitfresser ohne jede Software verschwindet: ein Formular, das niemand mehr braucht, eine Rückfrage, die durch eine bessere Vorlage entfällt, ein Bericht, den seit Jahren niemand liest. Diese Erkenntnisse sind unspektakulär und kosten nichts. Wir suchen sie bewusst zuerst, bevor über Anschaffungen gesprochen wird. Ein Beratungsunternehmen, das seine Software verkaufen will, überspringt diesen Teil – wir halten uns hier länger auf, weil es Ihr Geld spart.",
      },
      {
        heading: "Was Sie mitnehmen",
        body:
          "Am Ende steht ein Dokument, das nicht von uns geschrieben wurde, sondern von Ihrem Team: die Landkarte Ihrer Abläufe, die Rangfolge der Zeitfresser und je Punkt ein möglicher erster Schritt mit grober Aufwandseinschätzung. Weil es aus dem Team kommt, wird es auch getragen – das ist der Unterschied zu einem Beratungsbericht, der nach zwei Wochen in der Ablage liegt. Wenn Sie danach Unterstützung bei der Umsetzung wollen, gerne. Notwendig ist es nicht.",
      },
      {
        heading: "Förderungen für Weiterbildung und Digitalisierung",
        body:
          "Für Weiterbildung und Digitalisierungsvorhaben gibt es in Österreich regelmäßig Förderprogramme – über die Wirtschaftskammer, den waff, das AMS oder Landesförderungen, je nach Bundesland, Betriebsgröße und Zeitpunkt. Wir sind keine Förderberatung, weisen aber auf infrage kommende Programme hin und stellen Unterlagen so bereit, dass sie den üblichen Anforderungen genügen. Was aktuell verfügbar ist, prüfen Sie am besten direkt bei der jeweiligen Stelle.",
      },
    ],
    deliverables: [
      "Gemeinsame Landkarte Ihrer Abläufe",
      "Rangfolge der Zeitfresser nach Häufigkeit und Dauer",
      "Je Punkt ein möglicher erster Schritt",
      "Grobe Aufwands- und Nutzeneinschätzung",
      "Hinweis auf infrage kommende Förderprogramme",
    ],
    example: {
      title: "Der teuerste Zeitfresser war ein Formular",
      persona: "Typisches Szenario: Handelsbetrieb mit 12 Mitarbeitern",
      problem: "Die Geschäftsführung wollte eine neue Software anschaffen, war sich aber unsicher, welche.",
      solution: "Im Workshop zeigte die Ablauf-Landkarte, dass der größte Zeitverlust bei einer doppelten Erfassung zwischen zwei Abteilungen lag – behebbar durch eine Absprache, nicht durch Software.",
      result: "Die Anschaffung wurde verschoben, der eigentliche Engpass in zwei Wochen gelöst. Die Software kam später – für ein anderes, klar benanntes Problem.",
    },
    faq: [
      { q: "Müssen wir vorher etwas vorbereiten?", a: "Nein. Bringen Sie die Menschen mit, die die Arbeit tatsächlich machen. Alles Weitere entsteht im Workshop." },
      { q: "Wer sollte teilnehmen?", a: "Eine Mischung aus Entscheidungsebene und operativer Ebene. Ohne die Praxis fehlen die echten Umwege, ohne die Entscheidungsebene bleibt das Ergebnis folgenlos." },
      { q: "Verkaufen Sie uns danach Software?", a: "Nur wenn sie gebraucht wird, und wir sagen auch, wenn ein Standardprodukt besser passt als eine Eigenentwicklung. In vielen Workshops ist das Ergebnis, dass zunächst gar nichts gekauft werden muss." },
      { q: "Wie lange dauert es?", a: "Ein Halbtag reicht für einen Bereich, ein Ganztag für einen ganzen Betrieb bis etwa 20 Personen. Was in Ihrem Fall sinnvoll ist, klären wir im Vorgespräch." },
    ],
    relatedServices: ["it-consulting", "unternehmensberatung", "softwareentwicklung"],
    relatedSlugs: ["ki-workshop-unternehmen", "vortraege-keynotes"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "lehrlinge-digitalisierung",
    icon: "users",
    title: "Schulungen für Lehrlinge",
    navTitle: "Lehrlinge",
    tagline: "Digitale Grundlagen, die im Betrieb wirklich gebraucht werden.",
    audience: "Lehrlinge und Auszubildende, Ausbildungsbetriebe, Lehrwerkstätten",
    format: "Inhouse im Betrieb oder in der Ausbildungseinrichtung",
    duration: "Halbtag, Ganztag oder mehrteilige Reihe",
    outcomes: [
      "Digitale Werkzeuge des Betriebs sicher bedienen",
      "KI-Werkzeuge sinnvoll und mit Augenmaß nutzen",
      "Erkennen, wann eine digitale Auskunft nicht stimmt",
      "Grundregeln zu Datenschutz und Datensicherheit anwenden",
    ],
    summary:
      "Lehrlinge gelten als digital selbstverständlich – im Betrieb zeigt sich oft anderes. Wir vermitteln praxisnah, was im Arbeitsalltag gebraucht wird: von den betrieblichen Systemen bis zum verantwortungsvollen Umgang mit KI.",
    keywords: [
      "Lehrlinge Digitalisierung Schulung",
      "IT Schulung Lehrlinge Österreich",
      "KI Schulung Auszubildende",
      "Lehrlingsausbildung digitale Kompetenzen",
    ],
    metaTitle: "Digitale Schulung für Lehrlinge | Wien",
    metaDescription:
      "Praxisnahe Schulung für Lehrlinge: betriebliche Systeme, sicherer Umgang mit KI, Datenschutz-Grundlagen. Inhouse im Betrieb. Jetzt anfragen.",
    heroEyebrow: "Für Ausbildungsbetriebe",
    heroHeadline: "Mit dem Handy aufgewachsen heißt nicht: fit für den Betrieb",
    heroSub:
      "Junge Menschen bedienen soziale Netzwerke souverän. Ein Warenwirtschaftssystem, eine strukturierte Ablage oder die Frage, ob man Kundendaten in ein KI-Werkzeug tippen darf, sind etwas völlig anderes.",
    pains: [
      { title: "Der Mythos der digitalen Selbstverständlichkeit", text: "Weil Jugendliche geübt mit Apps umgehen, wird angenommen, sie kämen mit betrieblichen Systemen ebenso zurecht. Im Alltag zeigt sich regelmäßig, dass das nicht stimmt." },
      { title: "Niemand hat Zeit zum Erklären", text: "Einarbeitung passiert nebenbei zwischen zwei Aufträgen. Was dabei untergeht, bleibt jahrelang eine Lücke – und wird zur stillen Fehlerquelle." },
      { title: "KI wird benutzt, ohne dass jemand darüber spricht", text: "Lehrlinge nutzen KI-Werkzeuge für Aufgaben und Berichte. Ohne Anleitung entstehen Ergebnisse, die niemand geprüft hat – und ein Umgang, der später schwer zu korrigieren ist." },
      { title: "Datenschutz ist ein abstraktes Wort", text: "Solange niemand konkret zeigt, was passieren kann, bleibt es eine Vorschrift ohne Bedeutung – und wird entsprechend behandelt." },
    ],
    solutions: [
      { title: "An den Systemen Ihres Betriebs", text: "Wir schulen nicht an allgemeiner Software, sondern an dem, womit die Lehrlinge tatsächlich arbeiten. Was sie lernen, können sie am nächsten Tag anwenden." },
      { title: "KI mit Augenmaß statt Verbot", text: "Verbote werden umgangen. Wir zeigen, wofür KI im Ausbildungsalltag taugt, wofür nicht, und wie man eine falsche Antwort erkennt – ein Können, das ihnen ihr Berufsleben lang bleibt." },
      { title: "Datenschutz an echten Beispielen", text: "Statt Paragraphen konkrete Fälle aus dem Betriebsalltag: Was passiert, wenn diese Datei dorthin geht? Das bleibt hängen, weil es greifbar ist." },
    ],
    sections: [
      {
        heading: "Warum Ausbildungsbetriebe davon doppelt profitieren",
        body:
          "Der offensichtliche Nutzen: Lehrlinge werden schneller produktiv und machen weniger Fehler, die später jemand korrigieren muss. Der zweite Nutzen wird oft übersehen: Ausbildungsqualität ist inzwischen ein Argument bei der Lehrlingssuche. Betriebe, die neben dem Handwerk auch digitale Kompetenz vermitteln, haben es leichter, gute Bewerber zu bekommen und zu halten. Und ein dritter Punkt: Lehrlinge, die den sicheren Umgang mit digitalen Werkzeugen gelernt haben, tragen ihn in den Betrieb zurück – nicht selten sind sie es, die dem Team etwas zeigen.",
      },
      {
        heading: "Warum wir KI nicht ausklammern",
        body:
          "Manche Ausbildungsbetriebe würden das Thema am liebsten verbieten. Das funktioniert nicht: Die Werkzeuge sind kostenlos, überall verfügbar und werden ohnehin genutzt – nur eben unbeaufsichtigt. Sinnvoller ist, den Umgang zu begleiten. Wir üben mit den Lehrlingen, wann ein KI-Ergebnis brauchbar ist und wann nicht, warum eine selbstsicher formulierte Antwort trotzdem falsch sein kann, und was passieren muss, bevor etwas an einen Kunden geht. Das ist Medienkompetenz für den Beruf – und deutlich wirksamer als eine Regel, die niemand befolgt.",
      },
      {
        heading: "Aufbau und Formate",
        body:
          "Am häufigsten gefragt ist ein Ganztag im Betrieb, weil die eigenen Systeme vor Ort sind und die Gruppe ohnehin zusammenkommt. Für größere Ausbildungsbetriebe und Lehrwerkstätten bietet sich eine mehrteilige Reihe an – etwa vier Halbtage über ein Lehrjahr verteilt, mit Aufgaben dazwischen. Das wirkt nachhaltiger als ein einmaliger Termin, weil das Gelernte zwischendurch angewendet wird. Die Inhalte stimmen wir auf Lehrberuf und Ausbildungsjahr ab: Ein Bürolehrling braucht anderes als ein Metalltechniker.",
      },
    ],
    deliverables: [
      "Abstimmung auf Lehrberuf und Ausbildungsjahr",
      "Schulung an den Systemen Ihres Betriebs",
      "Sicherer Umgang mit KI-Werkzeugen",
      "Datenschutz und Datensicherheit an echten Beispielen",
      "Unterlagen zum Nachschlagen für die Lehrlinge",
    ],
    example: {
      title: "Aus stiller Unsicherheit wird Selbstverständlichkeit",
      persona: "Typisches Szenario: Ausbildungsbetrieb mit 6 Lehrlingen",
      problem:
        "Die Einarbeitung an den betrieblichen Systemen passierte nebenbei. Fehler bei der Erfassung häuften sich, und niemand sprach darüber, wie KI-Werkzeuge genutzt wurden.",
      solution:
        "Ganztägige Schulung an den eigenen Systemen, dazu ein Praxisteil zum Erkennen falscher KI-Antworten und zu Datenschutz an konkreten Fällen aus dem Betrieb.",
      result:
        "Die Lehrlinge arbeiten sicherer an den Systemen, und der Umgang mit KI ist offen besprochen statt heimlich – mit klaren Regeln, was geprüft werden muss.",
    },
    faq: [
      { q: "Ab welchem Lehrjahr ist das sinnvoll?", a: "Ab dem ersten. Je früher der saubere Umgang sitzt, desto weniger muss später korrigiert werden. Die Inhalte passen wir dem Ausbildungsstand an." },
      { q: "Können mehrere Betriebe gemeinsam buchen?", a: "Ja, das ist bei kleineren Betrieben mit ein oder zwei Lehrlingen oft die sinnvollste Variante. Dann schulen wir an allgemein gebräuchlichen Werkzeugen statt an einem einzelnen Betriebssystem." },
      { q: "Gibt es Förderungen für Lehrlingsausbildung?", a: "In Österreich bestehen verschiedene Förderungen für die betriebliche Weiterbildung von Lehrlingen, unter anderem über die Lehrstellenförderung. Wir weisen auf infrage kommende Programme hin; die Antragstellung läuft über Sie." },
      { q: "Arbeiten Sie auch mit Berufsschulen zusammen?", a: "Ja, sowohl mit Ausbildungsbetrieben als auch mit Bildungseinrichtungen. Der Aufbau unterscheidet sich, weil in der Schule nicht an betrieblichen Systemen gearbeitet werden kann." },
    ],
    relatedServices: ["kuenstliche-intelligenz", "it-consulting"],
    relatedSlugs: ["schulworkshops-ki", "ki-workshop-unternehmen"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "schulworkshops-ki",
    icon: "sparkles",
    title: "Workshops für Schulen",
    navTitle: "Schulen",
    tagline: "KI verstehen, statt sie nur zu benutzen.",
    audience: "Schülerinnen und Schüler ab der Sekundarstufe, Lehrkräfte",
    format: "Vor Ort in der Schule, als Einzeltermin oder Projekttag",
    duration: "Doppelstunde, Halbtag oder Projekttag",
    outcomes: [
      "Grundverständnis, wie KI-Systeme zu ihren Antworten kommen",
      "Falsche oder erfundene Antworten erkennen",
      "KI beim Lernen sinnvoll einsetzen statt Aufgaben abzugeben",
      "Einschätzen, welche Berufsbilder sich verändern",
    ],
    summary:
      "Schülerinnen und Schüler nutzen KI längst für Hausaufgaben. Der Workshop vermittelt, wie diese Systeme funktionieren, warum sie überzeugend falsch liegen können und wie man sie beim Lernen einsetzt, ohne das Denken abzugeben.",
    keywords: [
      "KI Workshop Schule",
      "künstliche Intelligenz Unterricht Wien",
      "Workshop Schüler Digitalisierung",
      "KI Projekttag Schule Österreich",
    ],
    metaTitle: "KI-Workshop für Schulen | Wien",
    metaDescription:
      "Workshop für Schülerinnen und Schüler: Wie KI funktioniert, warum sie falsch liegen kann und wie man sie beim Lernen sinnvoll nutzt. Jetzt anfragen.",
    heroEyebrow: "Für Schulen",
    heroHeadline: "Sie benutzen KI längst. Verstehen tun sie sie selten.",
    heroSub:
      "Für Hausaufgaben, Referate und Zusammenfassungen ist KI in den Klassen angekommen. Was fehlt, ist das Verständnis dafür, wie diese Systeme zu ihren Antworten kommen – und warum sie so überzeugend danebenliegen können.",
    pains: [
      { title: "Abschreiben ohne es zu merken", text: "Ein KI-Text fühlt sich nach eigener Arbeit an, weil man ihn angefordert hat. Wo die Grenze zur Täuschung verläuft, ist vielen nicht klar." },
      { title: "Blindes Vertrauen in flüssige Antworten", text: "Was gut formuliert ist, wirkt richtig. Genau darin liegt die Gefahr: KI erfindet Quellen, Jahreszahlen und Zitate in makellosem Deutsch." },
      { title: "Verbote wirken nicht", text: "Die Werkzeuge sind kostenlos und überall verfügbar. Ein Verbot verlagert die Nutzung nur dorthin, wo niemand mehr begleitet." },
      { title: "Lehrkräfte stehen allein da", text: "Viele möchten das Thema aufgreifen, haben aber weder Zeit für die Einarbeitung noch Material, das zum Lehrplan passt." },
    ],
    solutions: [
      { title: "Verstehen statt bedienen", text: "Wir erklären altersgerecht, wie Sprachmodelle funktionieren – dass sie Wahrscheinlichkeiten für das nächste Wort berechnen und nicht in einer Datenbank nachschlagen. Wer das begriffen hat, versteht auch die Fehler." },
      { title: "Falsche Antworten selbst finden", text: "Der wirksamste Teil: Die Klasse lässt sich Antworten geben und prüft sie nach. Der Moment, in dem eine erfundene Quelle auffliegt, wirkt stärker als jede Warnung." },
      { title: "Sinnvoll nutzen lernen", text: "KI als Erklärer, Übungspartner oder Ideengeber – nicht als Ersatz für die eigene Arbeit. Diesen Unterschied erarbeiten wir gemeinsam statt ihn zu verordnen." },
    ],
    sections: [
      {
        heading: "Wie ein Schulworkshop abläuft",
        body:
          "Wir beginnen mit einer Frage an die Klasse: Wer hat KI schon benutzt, und wofür? Die Antworten sind offener, als Lehrkräfte erwarten, und bilden die Grundlage für den Rest. Danach erklären wir in einfachen Bildern, was im Inneren passiert – ohne Mathematik, aber ohne zu vereinfachen bis zur Unwahrheit. Der Hauptteil ist praktisch: Die Klasse stellt Aufgaben, prüft die Antworten nach und dokumentiert die Fehler, die sie findet. Zum Abschluss erarbeiten wir gemeinsam Regeln für den eigenen Gebrauch – die von den Schülern kommen und deshalb eher gelten als eine Hausordnung.",
      },
      {
        heading: "Was das mit Berufsorientierung zu tun hat",
        body:
          "Auf Wunsch nehmen wir einen Teil zur Berufsorientierung dazu. Nicht als Angstszenario, sondern nüchtern: Welche Tätigkeiten verändern sich absehbar, welche Fähigkeiten gewinnen an Bedeutung, und warum ist technisches Grundverständnis auch in nicht-technischen Berufen nützlich. Das ist besonders in Abschlussklassen gefragt und bringt gelegentlich Berufsbilder ins Gespräch, die vorher niemand auf dem Schirm hatte. Wir werben dabei nicht für unser Unternehmen – ein Schulworkshop ist keine Rekrutierungsveranstaltung.",
      },
      {
        heading: "Auch für Lehrkräfte",
        body:
          "Auf Wunsch bieten wir einen eigenen Termin für das Kollegium an. Dort geht es um andere Fragen: Wie erkenne ich KI-Texte, und wie sicher ist diese Erkennung eigentlich? Wie stelle ich Aufgaben, die durch KI nicht sinnlos werden? Welche Regeln sind praktikabel? Erfahrungsgemäß ist der Bedarf hier groß, weil die Werkzeuge schneller in den Klassen ankamen als die Handreichungen dazu.",
      },
    ],
    deliverables: [
      "Altersgerechte Erklärung, wie KI-Systeme arbeiten",
      "Praxisteil: falsche Antworten selbst aufspüren",
      "Gemeinsam erarbeitete Regeln für den eigenen Gebrauch",
      "Optional: Teil zur Berufsorientierung",
      "Optional: eigener Termin für das Kollegium",
    ],
    example: {
      title: "Der Moment, in dem die erfundene Quelle auffliegt",
      persona: "Typisches Szenario: Klasse der Sekundarstufe",
      problem:
        "Die Klasse nutzte KI selbstverständlich für Referate, ohne Ergebnisse zu hinterfragen. Formulierungen wurden übernommen, Quellen nicht geprüft.",
      solution:
        "Nach einer altersgerechten Erklärung ließ die Klasse sich Antworten samt Quellenangaben geben und prüfte diese nach – mehrere Quellen existierten nicht.",
      result:
        "Aus selbstverständlichem Vertrauen wurde gesunde Skepsis. Die Regeln für den eigenen Gebrauch kamen anschließend aus der Klasse selbst.",
    },
    faq: [
      { q: "Ab welchem Alter ist das sinnvoll?", a: "Ab etwa der Sekundarstufe. Jüngere Gruppen sind möglich, dann liegt der Schwerpunkt stärker auf dem Erkennen falscher Angaben und weniger auf der Funktionsweise." },
      { q: "Brauchen die Schüler Geräte?", a: "Für den Praxisteil ist es hilfreich, wenn zumindest in Kleingruppen ein Gerät verfügbar ist. Geht das nicht, führen wir vor und die Klasse prüft gemeinsam mit – das funktioniert auch." },
      { q: "Was kostet ein Schulworkshop?", a: "Für Schulen bieten wir vergünstigte Konditionen an. Den konkreten Rahmen klären wir im Vorgespräch, abhängig von Format und Umfang." },
      { q: "Machen Sie auch Projekttage oder Projektwochen?", a: "Ja. Ein Projekttag erlaubt deutlich mehr eigenes Arbeiten der Schüler, etwa eine kleine eigene Untersuchung zur Verlässlichkeit von KI-Antworten." },
    ],
    relatedServices: ["kuenstliche-intelligenz"],
    relatedSlugs: ["lehrlinge-digitalisierung", "vortraege-keynotes"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "vortraege-keynotes",
    icon: "quote",
    title: "Vorträge & Keynotes",
    navTitle: "Vorträge",
    tagline: "KI und Digitalisierung – verständlich und ohne Hype.",
    audience: "Verbände, Netzwerke, Firmenveranstaltungen, Bildungseinrichtungen",
    format: "Vor Ort oder online, Impuls oder Hauptvortrag",
    duration: "20 bis 60 Minuten, auf Wunsch mit Fragerunde",
    outcomes: [
      "Eine realistische Einordnung, was KI heute leistet",
      "Konkrete Beispiele aus der Praxis statt abstrakter Szenarien",
      "Verständnis für die Grenzen und Risiken",
      "Ein bis zwei Ansatzpunkte für das eigene Unternehmen",
    ],
    summary:
      "Vorträge zu KI und Digitalisierung für Verbände, Netzwerke und Firmenveranstaltungen: verständlich, mit Beispielen aus echten Projekten und ohne die üblichen Untergangs- oder Heilsversprechen.",
    keywords: [
      "KI Vortrag buchen Österreich",
      "Speaker künstliche Intelligenz Wien",
      "Vortrag Digitalisierung KMU",
      "Keynote KI Unternehmen",
    ],
    metaTitle: "Vorträge zu KI & Digitalisierung | Wien",
    metaDescription:
      "Vorträge zu KI und Digitalisierung für Verbände, Netzwerke und Firmenevents – verständlich, praxisnah, ohne Hype. Jetzt Termin anfragen.",
    heroEyebrow: "Vortrag",
    heroHeadline: "Ein Vortrag, nach dem die Zuhörer etwas mitnehmen",
    heroSub:
      "Die meisten KI-Vorträge schwanken zwischen Weltuntergang und Wunderversprechen. Beides hilft niemandem weiter. Wir sprechen darüber, was heute tatsächlich funktioniert, was nicht, und was das für ein Unternehmen dieser Größe bedeutet.",
    pains: [
      { title: "Zu abstrakt", text: "Vorträge über Megatrends und Zukunftsszenarien sind unterhaltsam und folgenlos. Am Montag weiß niemand, was er anders machen soll." },
      { title: "Zu technisch", text: "Sobald Architekturdiagramme erscheinen, steigt der Großteil des Publikums aus – gerade die Entscheider, die eigentlich gemeint waren." },
      { title: "Verkaufsveranstaltung im Tarnanzug", text: "Ein Vortrag, der in eine Produktvorführung mündet, verbrennt das Vertrauen des Veranstalters gleich mit." },
      { title: "Keine Verbindung zur Realität der Zuhörer", text: "Beispiele von Konzernen helfen einem Betrieb mit zwölf Mitarbeitern nicht. Die Frage ist immer: Was heißt das für uns?" },
    ],
    solutions: [
      { title: "Beispiele aus echten Projekten", text: "Wir sprechen über Vorhaben, die wir selbst umgesetzt haben – auch über die, bei denen sich herausstellte, dass KI die falsche Antwort war." },
      { title: "Auf das Publikum zugeschnitten", text: "Ein Vortrag vor Handwerksbetrieben sieht anders aus als einer vor Steuerberatern. Wir stimmen Beispiele und Schwerpunkte vorab mit dem Veranstalter ab." },
      { title: "Kein Verkauf von der Bühne", text: "Ein Vortrag ist ein Vortrag. Wer danach mit uns sprechen will, findet uns – dafür braucht es keinen Produktteil am Schluss." },
    ],
    sections: [
      {
        heading: "Mögliche Themen",
        body:
          "Am häufigsten angefragt wird eine nüchterne Einordnung: Was können die heutigen KI-Systeme, was können sie nicht, und woran erkennt man den Unterschied. Ebenfalls gefragt sind Vorträge zu Digitalisierung im Klein- und Mittelbetrieb – wo Zeit tatsächlich verloren geht und welche Schritte sich zuerst lohnen. Für Bildungseinrichtungen bieten sich Themen rund um den Umgang mit KI im Lernen und veränderte Berufsbilder an. Und für Veranstaltungen mit gemischtem Publikum eignet sich die Frage, was Unternehmen bei KI konkret beachten müssen – von Datenschutz bis zur Prüfung der Ergebnisse. Andere Schwerpunkte stimmen wir gerne ab.",
      },
      {
        heading: "Warum wir auch über Fehlschläge sprechen",
        body:
          "In fast jedem Vortrag ist der Teil, der am längsten nachwirkt, der über Vorhaben, die nicht funktioniert haben – etwa wenn sich zeigte, dass ein Ablauf zu selten vorkommt, um Automatisierung zu rechtfertigen, oder dass ein Datenschutzproblem den ganzen Ansatz kippte. Solche Beispiele sind für Zuhörer nützlicher als Erfolgsgeschichten, weil sie eine Prüffrage mitgeben statt eines Vorbilds. Und sie sind glaubwürdiger: Ein Vortragender, der nur Erfolge kennt, hat entweder wenig gemacht oder erzählt nicht alles.",
      },
      {
        heading: "Rahmen und Ablauf",
        body:
          "Üblich sind 20 bis 30 Minuten als Impuls oder 45 bis 60 Minuten als Hauptvortrag, jeweils mit anschließender Fragerunde – die erfahrungsgemäß der wertvollste Teil ist, weil dort die konkreten Fälle aus dem Publikum kommen. Wir sprechen vorab mit dem Veranstalter über Publikum, Anlass und gewünschten Schwerpunkt, damit die Beispiele passen. Vor Ort in Wien und Umgebung ohne Aufschlag, darüber hinaus mit Anfahrt; online ebenfalls möglich.",
      },
    ],
    deliverables: [
      "Abstimmung von Thema und Beispielen mit dem Veranstalter",
      "Impulsvortrag oder Hauptvortrag nach Bedarf",
      "Beispiele aus echten Projekten, auch aus gescheiterten",
      "Fragerunde im Anschluss",
      "Auf Wunsch Folien und Nachlese für die Teilnehmenden",
    ],
    example: {
      title: "Die Fragerunde war der eigentliche Vortrag",
      persona: "Typisches Szenario: Netzwerkveranstaltung für Klein- und Mittelbetriebe",
      problem:
        "Das Publikum war beim Thema KI zwischen Skepsis und Überforderung gespalten. Frühere Vorträge waren zu abstrakt geblieben.",
      solution:
        "Ein Impulsvortrag mit Beispielen aus Betrieben vergleichbarer Größe, ausdrücklich auch mit Vorhaben, die nicht funktioniert haben – gefolgt von einer offenen Fragerunde.",
      result:
        "Die Fragerunde dauerte länger als der Vortrag. Mehrere Teilnehmende nahmen einen konkreten ersten Schritt für den eigenen Betrieb mit.",
    },
    faq: [
      { q: "Was kostet ein Vortrag?", a: "Das hängt von Format, Dauer, Ort und Art der Veranstaltung ab. Für Bildungseinrichtungen und gemeinnützige Veranstalter gelten andere Konditionen als für Firmenveranstaltungen. Sprechen Sie uns einfach an." },
      { q: "Können Sie ein Thema übernehmen, das wir vorgeben?", a: "Innerhalb unseres Fachgebiets gerne. Wenn ein Thema außerhalb dessen liegt, wovon wir wirklich etwas verstehen, sagen wir ab – ein oberflächlicher Vortrag nützt Ihrer Veranstaltung nicht." },
      { q: "Halten Sie auch Vorträge online?", a: "Ja. Online funktioniert besonders gut für Impulsvorträge; für längere Formate mit Diskussion ist vor Ort meist wirksamer." },
      { q: "Ist der Vortrag auch für Publikum ohne technisches Vorwissen geeignet?", a: "Ja, das ist der Regelfall. Wir setzen kein Vorwissen voraus und verzichten bewusst auf Fachjargon, ohne dabei ungenau zu werden." },
    ],
    relatedServices: ["kuenstliche-intelligenz", "unternehmensberatung"],
    relatedSlugs: ["ki-workshop-unternehmen", "schulworkshops-ki"],
  },
];

export function getWorkshop(slug: string) {
  return workshops.find((w) => w.slug === slug);
}
